/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import i18n from '@/i18n/i18n';
import { useUserId } from '@/api/hooks';
import { getUserSettings } from '@/api/settings';
import { useUiStore, type ThemePreference } from '@/stores/uiStore';

const THEMES: ThemePreference[] = ['system', 'light', 'dark'];

/**
 * Applies the user's saved settings once they load, so the app opens in the language and theme they
 * last chose. These are restored from the backend (not localStorage), mirroring the Angular
 * `UserSettingsService`, so clearing browser storage never loses them. Applied once per user;
 * if the signed-in account changes, the new user's settings are re-applied. Further changes are
 * driven by the header {@link LanguageMenu} and the settings dialog.
 */
export function useSyncUserSettings(): void {
  const gmin = useUserId();
  const setTheme = useUiStore((s) => s.setTheme);
  const setPlant = useUiStore((s) => s.setPlant);
  const appliedFor = useRef<string | null>(null);
  const { data } = useQuery({
    queryKey: ['user-settings', gmin],
    queryFn: () => getUserSettings(gmin),
    enabled: Boolean(gmin),
    retry: false,
  });

  useEffect(() => {
    // Re-apply when the account changes so a second user doesn't inherit the first user's prefs.
    if (!data || appliedFor.current === gmin) return;
    appliedFor.current = gmin;

    const code = data.languageDisplay?.languageCode;
    if (code && i18n.language !== code) {
      void i18n.changeLanguage(code);
    }

    const theme = data.themeSetting;
    if (theme && THEMES.includes(theme)) {
      setTheme(theme);
    }

    const plant = data.plant;
    if (plant?.plantCode) {
      setPlant({
        plantCode: plant.plantCode,
        plantName: plant.plantName ?? plant.plantCode,
        eoltCountryPlantLine: plant.eoltCountryPlantLine,
      });
    }
  }, [data, gmin, setTheme, setPlant]);
}
